import { cn } from '@/lib/utils';
import { useAuthenticatedImage } from '@/lib/authenticatedImage';

interface Props {
  src?: string | null;
  alt?: string;
  fallback: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const SIZES = {
  xs: 'h-6 w-6 text-[9px]',
  sm: 'h-8 w-8 text-[10px]',
  md: 'h-10 w-10 text-xs',
  lg: 'h-12 w-12 text-sm',
  xl: 'h-16 w-16 text-base',
};

export function Avatar({ src, alt, fallback, className, size = 'md' }: Props) {
  // A photo served by our own API sits behind the session — an `<img>` cannot present a token,
  // so the bytes are fetched first. Until they arrive (or if they never do) the initials show,
  // which is what the person saw anyway when avatars silently 401'd.
  const resolved = useAuthenticatedImage(src);

  if (resolved) {
    return (
      <img
        src={resolved}
        alt={alt ?? fallback}
        className={cn('rounded-full object-cover ring-2 ring-white/80 shadow-sm', SIZES[size], className)}
      />
    );
  }
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full bg-gradient-to-br from-brand-50 to-brand-100 font-bold uppercase text-brand-600 dark:text-brand-400 ring-2 ring-white/80 shadow-sm',
        SIZES[size],
        className,
      )}
    >
      {fallback.slice(0, 2)}
    </div>
  );
}
